import styled from "styled-components";
import React from "react";
import { CheckCircle2, Clock, Loader2, QrCode, X } from "lucide-react";
import { Link, router } from "@inertiajs/react";

import { useRoom } from "../../../../contexts/room";
import { useLanguage } from "../../../../contexts/language";
import { PageHeader } from "../../../../components/page-header";
import { unmodule } from "../../../../util";

const checkinScanImg = unmodule(
  require("../../../../assets/checkin-scan-qr.jpg"),
);

const TUTORIAL_EN = [
  {
    title: "Find your reservation",
    desc: "Tap the screen, choose Check-in, then enter your booking code. No code? Choose Other method and enter your name and check-out date, or ask the Front Office.",
  },
  {
    title: "Scan your ID / passport",
    desc: "Scan your KTP, driver's license, or passport and wait a moment. If the scan fails, reposition your ID and try again.",
  },
  {
    title: "Finalize registration",
    desc: "Enter your phone number and email address, then press Next.",
  },
  {
    title: "Terms & conditions",
    desc: "The fields are filled automatically. Review the guest rules and terms, then press Next to accept them.",
  },
  {
    title: "Print your room key",
    desc: "Check your details, room, and check-in/out dates. If everything is correct, press Print Card and wait for your key.",
  },
];

const TUTORIAL_ID = [
  {
    title: "Cari reservasi Anda",
    desc: "Ketuk layar, pilih Check-in, lalu masukkan kode pemesanan. Tidak punya kode? Pilih Cara lain dan masukkan nama serta tanggal check-out, atau minta ke Front Office.",
  },
  {
    title: "Scan KTP / paspor",
    desc: "Pindai KTP, SIM, atau paspor Anda dan tunggu sebentar. Jika gagal, tempatkan ulang identitas sesuai instruksi lalu coba lagi.",
  },
  {
    title: "Selesaikan registrasi",
    desc: "Masukkan nomor telepon dan alamat email Anda, lalu tekan Lanjutkan.",
  },
  {
    title: "Syarat & ketentuan",
    desc: "Isian terisi otomatis. Baca kembali peraturan dan ketentuan tamu, lalu tekan Lanjutkan untuk menyetujuinya.",
  },
  {
    title: "Cetak kunci kamar",
    desc: "Periksa detail, kamar, dan tanggal check-in/out. Jika sudah benar, tekan Cetak Kartu dan tunggu kunci Anda.",
  },
];

const COPY = {
  en: {
    title: "Self Check-in Deposit",
    subtitle:
      "Find your booking, verify it's you, then pay your deposit online — no front desk needed.",
    back: "Back to home",
    byVoucher: "Booking / voucher code",
    byName: "Name + check-out date",
    voucherLabel: "Voucher or booking code",
    nameLabel: "Full name",
    checkoutLabel: "Check-out date",
    find: "Find my booking",
    findDepositBtn: "Pay deposit",
    findRefundBtn: "Refund deposit",
    sendCode: "Send my code",
    codeLabel: "Enter the 6-character code",
    verify: "Verify",
    resend: "Resend code",
    noChannel:
      "We couldn't find a phone or email on this booking. Please see the Front Office — they can send your code.",
    sentTo: "We sent a code to",
    contactsTitle: "Confirm your contact details",
    contactsWhy:
      "We use these to contact you and to send your deposit refund automatically after checkout — your WhatsApp receives the refund link and your email the payout details. Please make sure both are correct and active.",
    email: "Email",
    whatsapp: "WhatsApp number",
    consentLabel:
      "I confirm these details are correct and I consent to Hebe using my email and WhatsApp to communicate about my stay and to process my deposit and its automatic refund. I understand that mistakes in the data I enter are my own responsibility.",
    consentRequired: "Please tick the consent box to continue.",
    readPolicy: "Read our Privacy Policy",
    continue: "Continue",
    confirmTitle: "Please confirm your contact details",
    confirmIntro:
      "Everything about your deposit and its automatic refund will be sent to these. Double-check they are correct — your refund goes to the WhatsApp and email below.",
    confirmEmailLabel: "Email",
    confirmWhatsappLabel: "WhatsApp",
    confirmWarning:
      "If these are wrong you may not receive your deposit refund. Mistakes in what you enter are not our responsibility.",
    edit: "Edit",
    confirmContinue: "Confirm & continue",
    payTitle: "Your deposit",
    deposit: "Security deposit",
    fee: "Online payment service fee",
    feeNote: "A IDR 2,500 online service fee applies and is non-refundable.",
    earlyCheckin: "Add early check-in",
    total: "Total to pay",
    payNow: "Pay now",
    openPayment: "Open payment page",
    validUntil: "Pay before",
    expiredNotice:
      "Your payment link has expired. Generate a new one to continue.",
    regenerate: "Generate a new link",
    refundNote:
      "Deposit refunds are full only when housekeeping reports no room issue; if an issue is found the refund may be reduced (50% or 0%). The service fee is never refunded.",
    waitingTitle: "Waiting for your payment",
    waitingBody:
      "Finish paying in the payment window. This page updates automatically once we receive your payment — or check now.",
    checkStatus: "Check payment status",
    checking: "Checking…",
    notPaidYet:
      "We haven't received your payment yet. Please complete it, then check again.",
    nights: "nights",
    rooms: "rooms",
    step: "Step",
    of: "of",
    stepNames: {
      lookup: "Find your booking",
      otp: "Verify your identity",
      contacts: "Confirm your contacts",
      pay: "Pay your deposit",
    },
    how: {
      title: "How it works",
      s1: "Find your booking using your voucher code, or your name and check-out date.",
      s2: "Verify it's you with a one-time code we send to your phone or email.",
      s3: "Confirm your WhatsApp and email so we can return your deposit after checkout.",
      s4: "Review and pay your deposit online — then you're ready for self check-in.",
    },
    success: {
      title: "Deposit paid — you're set for self check-in",
      body: "Your security deposit is settled. Go to the self check-in machine once your room is ready (normally by 14:00).",
      tutorialTitle: "How to self check-in",
      tutorialNote: "The machine can be used after check-in time (14:00).",
      steps: TUTORIAL_EN,
      qrTitle: "Your booking QR code",
      qrInstruction:
        "Hold this QR up to the scanner on the self check-in machine — it fills in your booking code automatically, no typing. Tap the code to enlarge it for the scanner.",
      scanTitle: "Where to scan at the kiosk",
      scanNote:
        "At the self check-in machine, hold your booking QR up to the highlighted scanner in the middle — your booking code fills in automatically, no typing.",
      qrCodeLabel: "Booking code",
      qrZoomHint: "Tap to enlarge",
      qrCloseLabel: "Close",
      timesTitle: "Check-in & check-out",
      checkInLabel: "Check-in from",
      checkInValue: "14:00",
      checkOutLabel: "Check-out by",
      nextTitle: "While you're here",
      orderLink: "My orders",
      addOnLink: "Add-on facilities",
      fnbLink: "Food & drinks",
    },
    early: {
      title: "Check in early?",
      offer: "Add early check-in",
      availability:
        "Subject to availability — only offered when your room is already clean and ready right now.",
      significance:
        "Early check-in only helps if the extra time matters to you. If it's already close to 14:00 the benefit is small — it's your call.",
      button: "Pay for early check-in",
      opened:
        "We opened your early check-in payment. After paying, check its status below.",
      check: "Check early check-in status",
      paid: "Early check-in paid",
    },
  },
  id: {
    title: "Deposit Check-in Mandiri",
    subtitle:
      "Temukan pemesanan Anda, verifikasi identitas, lalu bayar deposit online — tanpa ke resepsionis.",
    back: "Kembali ke beranda",
    byVoucher: "Kode pemesanan / voucher",
    byName: "Nama + tanggal check-out",
    voucherLabel: "Kode voucher atau pemesanan",
    nameLabel: "Nama lengkap",
    checkoutLabel: "Tanggal check-out",
    find: "Cari pemesanan saya",
    findDepositBtn: "Bayar deposit",
    findRefundBtn: "Refund deposit",
    sendCode: "Kirim kode saya",
    codeLabel: "Masukkan kode 6 karakter",
    verify: "Verifikasi",
    resend: "Kirim ulang kode",
    noChannel:
      "Kami tidak menemukan nomor telepon atau email pada pemesanan ini. Silakan ke Front Office — mereka dapat mengirim kode Anda.",
    sentTo: "Kami mengirim kode ke",
    contactsTitle: "Konfirmasi kontak Anda",
    contactsWhy:
      "Kami menggunakan ini untuk menghubungi Anda dan mengirim pengembalian deposit secara otomatis setelah check-out — WhatsApp menerima tautan pengembalian, email menerima rincian payout. Pastikan keduanya benar dan aktif.",
    email: "Email",
    whatsapp: "Nomor WhatsApp",
    consentLabel:
      "Saya memastikan data ini benar dan menyetujui Hebe menggunakan email dan WhatsApp saya untuk berkomunikasi tentang masa inap saya serta memproses deposit dan pengembalian otomatisnya. Saya memahami bahwa kesalahan pada data yang saya masukkan adalah tanggung jawab saya sendiri.",
    consentRequired: "Centang kotak persetujuan untuk melanjutkan.",
    readPolicy: "Baca Kebijakan Privasi kami",
    continue: "Lanjut",
    confirmTitle: "Mohon konfirmasi kontak Anda",
    confirmIntro:
      "Semua tentang deposit dan pengembalian otomatisnya akan dikirim ke sini. Periksa kembali kebenarannya — pengembalian Anda dikirim ke WhatsApp dan email di bawah.",
    confirmEmailLabel: "Email",
    confirmWhatsappLabel: "WhatsApp",
    confirmWarning:
      "Jika data ini salah, Anda mungkin tidak menerima pengembalian deposit. Kesalahan pada data yang Anda masukkan bukan tanggung jawab kami.",
    edit: "Edit",
    confirmContinue: "Konfirmasi & lanjut",
    payTitle: "Deposit Anda",
    deposit: "Deposit keamanan",
    fee: "Biaya layanan pembayaran online",
    feeNote:
      "Biaya layanan online IDR 2.500 berlaku dan tidak dapat dikembalikan.",
    earlyCheckin: "Tambah early check-in",
    total: "Total pembayaran",
    payNow: "Bayar sekarang",
    openPayment: "Buka halaman pembayaran",
    validUntil: "Bayar sebelum",
    expiredNotice:
      "Tautan pembayaran Anda telah kedaluwarsa. Buat tautan baru untuk melanjutkan.",
    regenerate: "Buat tautan baru",
    refundNote:
      "Deposit dikembalikan penuh hanya jika housekeeping tidak menemukan masalah kamar; jika ada masalah, pengembalian dapat dikurangi (50% atau 0%). Biaya layanan tidak pernah dikembalikan.",
    waitingTitle: "Menunggu pembayaran Anda",
    waitingBody:
      "Selesaikan pembayaran di jendela pembayaran. Halaman ini diperbarui otomatis begitu pembayaran kami terima — atau cek sekarang.",
    checkStatus: "Cek status pembayaran",
    checking: "Memeriksa…",
    notPaidYet:
      "Pembayaran Anda belum kami terima. Silakan selesaikan, lalu cek lagi.",
    nights: "malam",
    rooms: "kamar",
    step: "Langkah",
    of: "dari",
    stepNames: {
      lookup: "Temukan pemesanan",
      otp: "Verifikasi identitas",
      contacts: "Konfirmasi kontak",
      pay: "Bayar deposit",
    },
    how: {
      title: "Cara kerjanya",
      s1: "Temukan pemesanan Anda dengan kode voucher, atau nama dan tanggal check-out.",
      s2: "Verifikasi identitas dengan kode sekali pakai yang kami kirim ke ponsel atau email Anda.",
      s3: "Konfirmasi WhatsApp dan email Anda agar kami dapat mengembalikan deposit setelah check-out.",
      s4: "Tinjau dan bayar deposit Anda online — lalu Anda siap untuk check-in mandiri.",
    },
    success: {
      title: "Deposit lunas — Anda siap check-in mandiri",
      body: "Deposit keamanan Anda sudah lunas. Datang ke mesin check-in mandiri begitu kamar Anda siap (umumnya pukul 14.00).",
      tutorialTitle: "Cara menggunakan mesin check-in mandiri",
      tutorialNote: "Mesin dapat digunakan setelah waktu check-in (14.00).",
      steps: TUTORIAL_ID,
      qrTitle: "Kode QR pemesanan Anda",
      qrInstruction:
        "Arahkan QR ini ke pemindai pada mesin check-in mandiri — kode pemesanan Anda akan terisi otomatis, tanpa mengetik. Ketuk kode untuk memperbesar agar mudah dipindai.",
      scanTitle: "Tempat memindai di mesin",
      scanNote:
        "Di mesin check-in mandiri, arahkan kode QR pemesanan ke pemindai yang ditandai di bagian tengah — kode pemesanan terisi otomatis, tanpa mengetik.",
      qrCodeLabel: "Kode pemesanan",
      qrZoomHint: "Ketuk untuk memperbesar",
      qrCloseLabel: "Tutup",
      timesTitle: "Check-in & check-out",
      checkInLabel: "Check-in mulai",
      checkInValue: "14.00",
      checkOutLabel: "Check-out paling lambat",
      nextTitle: "Selama Anda di sini",
      orderLink: "Pesanan saya",
      addOnLink: "Fasilitas tambahan",
      fnbLink: "Makanan & minuman",
    },
    early: {
      title: "Check-in lebih awal?",
      offer: "Tambah early check-in",
      availability:
        "Tergantung ketersediaan — hanya ditawarkan jika kamar Anda sudah bersih dan siap saat ini.",
      significance:
        "Early check-in hanya bermanfaat jika tambahan waktu berarti bagi Anda. Jika sudah mendekati pukul 14.00, manfaatnya kecil — keputusan ada pada Anda.",
      button: "Bayar early check-in",
      opened:
        "Kami membuka pembayaran early check-in Anda. Setelah membayar, cek statusnya di bawah.",
      check: "Cek status early check-in",
      paid: "Early check-in lunas",
    },
  },
};

const postJson = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body || {}),
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const getJson = (url) =>
  fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const formatDateTime = (iso, language) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(language === "id" ? "id-ID" : "en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (iso, language) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(
    language === "id" ? "id-ID" : "en-GB",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );
};

const SelfDeposit = (props) => {
  const { language } = useLanguage();
  const { applyBookingProfile } = useRoom();
  const c = COPY[language] || COPY.en;
  const buildingCode = props.building_code;
  const policyUrl =
    language === "id" ? "/id/hebe/privacy-policy" : "/hebe/privacy-policy";

  const [step, setStep] = React.useState("loading");
  const [flow, setFlow] = React.useState("deposit");
  const [mode, setMode] = React.useState("voucher");
  const [voucher, setVoucher] = React.useState("");
  const [name, setName] = React.useState("");
  const [checkoutDate, setCheckoutDate] = React.useState("");
  const [booking, setBooking] = React.useState(null);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [noChannel, setNoChannel] = React.useState(false);

  const [code, setCode] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [whatsapp, setWhatsapp] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [withEarlyCheckin, setWithEarlyCheckin] = React.useState(false);

  const [refreshBusy, setRefreshBusy] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [earlyOpened, setEarlyOpened] = React.useState(false);
  const [earlyPaid, setEarlyPaid] = React.useState(false);

  const depositPayment = (booking && booking.deposit_payment) || {};

  const seedContacts = (bk) => {
    if (!bk || !bk.contact_prefill) return;
    if (bk.contact_prefill.email)
      setEmail((v) => v || bk.contact_prefill.email);
    if (bk.contact_prefill.whatsapp)
      setWhatsapp((v) => v || bk.contact_prefill.whatsapp);
  };

  const stepForBooking = (bk, contactVerified) => {
    if (bk && bk.deposit_payment && bk.deposit_payment.paid) return "done";
    return contactVerified ? "pay" : "contacts";
  };

  const refreshSession = () =>
    getJson("/api/v1/guest_portal/session").then((res) => {
      if (res.ok && res.json.data && res.json.data.authenticated) {
        setBooking(res.json.data.booking);
        return res.json.data.booking;
      }
      return null;
    });

  React.useEffect(() => {
    getJson("/api/v1/guest_portal/session")
      .then((res) => {
        if (res.ok && res.json.data && res.json.data.authenticated) {
          const bk = res.json.data.booking;
          setBooking(bk);
          seedContacts(bk);
          setStep(stepForBooking(bk, res.json.data.contact_verified));
        } else {
          setStep("lookup");
        }
      })
      .catch(() => setStep("lookup"));
  }, []);

  React.useEffect(() => {
    if (step !== "waiting") return undefined;
    const id = setInterval(() => {
      refreshSession().then((bk) => {
        if (bk && bk.deposit_payment && bk.deposit_payment.paid)
          setStep("done");
      });
    }, 5000);
    return () => clearInterval(id);
  }, [step]);

  React.useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const run = (fn) => {
    setBusy(true);
    setError("");
    return fn().finally(() => setBusy(false));
  };

  const doLookup = () =>
    run(() =>
      postJson("/api/v1/guest_portal/lookup", {
        voucher,
        name,
        check_out_date: checkoutDate,
      }).then((res) => {
        if (!res.ok)
          return setError(
            (res.json.message_i18n && res.json.message_i18n[language]) ||
              res.json.message,
          );
        setFlow("deposit");
        setBooking(res.json.data);
        return requestOtp(res.json.data.booking_id, "deposit");
      }),
    );

  // "Find booking for refund deposit" — finds an already-checked-out booking with an unfinished
  // online deposit refund, then runs the same OTP gate (context=refund) before the refund flow.
  const doRefundLookup = () =>
    run(() =>
      postJson("/api/v1/guest_portal/refund_lookup", {
        voucher,
        name,
        check_out_date: checkoutDate,
      }).then((res) => {
        if (!res.ok)
          return setError(
            (res.json.message_i18n && res.json.message_i18n[language]) ||
              res.json.message,
          );
        setFlow("refund");
        setBooking(res.json.data);
        return requestOtp(res.json.data.booking_id, "refund");
      }),
    );

  const requestOtp = (bookingId, context = "deposit") =>
    postJson("/api/v1/guest_portal/otp/request", {
      booking_id: bookingId,
      context,
    }).then((res) => {
      setNoChannel(Boolean(res.json.data && res.json.data.no_channel));
      setStep("otp");
    });

  const verifyOtp = () =>
    run(() =>
      postJson("/api/v1/guest_portal/otp/verify", {
        booking_id: booking.booking_id,
        code,
        context: flow,
      }).then((res) => {
        if (!res.ok)
          return setError(
            `${res.json.message} (${res.json.remaining_attempts ?? 0} left)`,
          );
        // Now that the guest is verified against a real booking, overwrite the locally-typed
        // profile (name + room) with the authoritative booking data (informative display only).
        applyBookingProfile({
          name: booking.guest_name,
          roomNumber: booking.room_code,
          buildingCode,
        });
        // Refund flow → straight to the booking-detail refund page. Deposit flow → the bookings
        // list so a guest with several same-day stays can pick which one to pay.
        const target = flow === "refund" ? "booking-detail" : "bookings";
        router.visit(`/hebe/${buildingCode}/hgs/${target}`);
      }),
    );

  const openConfirm = () => {
    if (!consent) return setError(c.consentRequired);
    setError("");
    setShowConfirm(true);
  };

  const saveContacts = () =>
    run(() =>
      postJson("/api/v1/guest_portal/contacts/save", {
        email,
        whatsapp,
        consent,
      }).then((res) => {
        if (!res.ok)
          return setError(
            (res.json.message_i18n && res.json.message_i18n[language]) ||
              res.json.message,
          );
        setShowConfirm(false);
        setStep("pay");
      }),
    );

  const startCheckout = () =>
    run(() =>
      postJson("/api/v1/guest_portal/checkout", {
        early_checkin: withEarlyCheckin,
        building_code: buildingCode,
      }).then((res) => {
        if (!res.ok) return setError(res.json.message);
        if (res.json.data.payment_link_url)
          window.open(res.json.data.payment_link_url, "_blank");
        return refreshSession().then(() => setStep("waiting"));
      }),
    );

  const openExisting = () => {
    if (depositPayment.payment_link_url)
      window.open(depositPayment.payment_link_url, "_blank");
    setStep("waiting");
  };

  const checkStatus = () => {
    setRefreshBusy(true);
    setError("");
    postJson("/api/v1/guest_portal/payments/refresh")
      .then((res) => {
        if (res.ok && res.json.data && res.json.data.paid)
          return refreshSession().then(() => setStep("done"));
        setError(c.notPaidYet);
        setCooldown(5);
        return null;
      })
      .finally(() => setRefreshBusy(false));
  };

  const buyEarlyCheckin = () =>
    run(() =>
      postJson("/api/v1/guest_portal/early_checkin", {
        building_code: buildingCode,
      }).then((res) => {
        if (!res.ok)
          return setError(
            (res.json.message_i18n && res.json.message_i18n[language]) ||
              res.json.message,
          );
        const links = (res.json.data && res.json.data.links) || [];
        links.forEach(
          (l) =>
            l.payment_link_url && window.open(l.payment_link_url, "_blank"),
        );
        setEarlyOpened(true);
      }),
    );

  const checkEarly = () =>
    postJson("/api/v1/guest_portal/early_checkin/refresh").then((res) => {
      if (res.ok && res.json.data && res.json.data.paid) setEarlyPaid(true);
    });

  const totalToPay =
    booking && booking.deposit
      ? booking.deposit.total_idr +
        (withEarlyCheckin && booking.early_checkin
          ? booking.early_checkin.fee_idr
          : 0)
      : 0;

  return (
    <Page>
      <PageHeader
        title={c.title}
        backPath={`/hebe/${buildingCode}/hgs/bookings`}
        showGuestInfo={false}
        buildingCode={buildingCode}
      />
      <Inner>
        {step === "loading" ? (
          <LoadingBox>
            <Loader2 size={24} className="spin" />
          </LoadingBox>
        ) : null}

        {step !== "loading" ? <Lead>{c.subtitle}</Lead> : null}
        {error ? <ErrorBox>{error}</ErrorBox> : null}

        {step === "lookup" ? <HowItWorks c={c} /> : null}
        {step !== "loading" ? <StepIndicator step={step} c={c} /> : null}

        {step === "lookup" ? (
          <Card>
            <Tabs>
              <Tab
                $active={mode === "voucher"}
                onClick={() => setMode("voucher")}
              >
                {c.byVoucher}
              </Tab>
              <Tab $active={mode === "name"} onClick={() => setMode("name")}>
                {c.byName}
              </Tab>
            </Tabs>
            {mode === "voucher" ? (
              <Field>
                <label>{c.voucherLabel}</label>
                <input
                  value={voucher}
                  onChange={(e) => setVoucher(e.target.value)}
                />
              </Field>
            ) : (
              <>
                <Field>
                  <label>{c.nameLabel}</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>
                <Field>
                  <label>{c.checkoutLabel}</label>
                  <input
                    type="date"
                    value={checkoutDate}
                    onChange={(e) => setCheckoutDate(e.target.value)}
                  />
                </Field>
              </>
            )}
            <FindHeading>{c.find}</FindHeading>
            <ButtonRow>
              <Primary disabled={busy} onClick={doLookup}>
                {busy ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  c.findDepositBtn
                )}
              </Primary>
              <OutlineButton disabled={busy} onClick={doRefundLookup}>
                {busy ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  c.findRefundBtn
                )}
              </OutlineButton>
            </ButtonRow>
          </Card>
        ) : null}

        {step === "otp" ? (
          <Card>
            <Summary booking={booking} c={c} />
            {noChannel ? (
              <Note>{c.noChannel}</Note>
            ) : (
              <>
                <Note>
                  {c.sentTo} {booking.channels.whatsapp || ""}{" "}
                  {booking.channels.email || ""}
                </Note>
                <Field>
                  <label>{c.codeLabel}</label>
                  <input
                    value={code}
                    maxLength={6}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                  />
                </Field>
                <Primary disabled={busy} onClick={verifyOtp}>
                  {c.verify}
                </Primary>
                <Secondary onClick={() => requestOtp(booking.booking_id, flow)}>
                  {c.resend}
                </Secondary>
              </>
            )}
          </Card>
        ) : null}

        {step === "contacts" ? (
          <Card>
            <Note>{c.contactsWhy}</Note>
            <Field>
              <label>{c.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field>
              <label>{c.whatsapp}</label>
              <input
                type="tel"
                inputMode="numeric"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </Field>
            <ConsentRow>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                {c.consentLabel}{" "}
                <PolicyLink href={policyUrl} target="_blank" rel="noopener">
                  {c.readPolicy}
                </PolicyLink>
              </span>
            </ConsentRow>
            <Primary disabled={busy || !consent} onClick={openConfirm}>
              {c.continue}
            </Primary>
          </Card>
        ) : null}

        {step === "pay" && booking ? (
          <Card>
            <Summary booking={booking} c={c} />
            <Row>
              <span>{c.deposit}</span>
              <span>{rupiah(booking.deposit.base_idr)}</span>
            </Row>
            <Row>
              <span>{c.fee}</span>
              <span>{rupiah(booking.deposit.fee_idr)}</span>
            </Row>
            {booking.early_checkin && booking.early_checkin.available ? (
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={withEarlyCheckin}
                  onChange={(e) => setWithEarlyCheckin(e.target.checked)}
                />
                <span>
                  {c.earlyCheckin} (+{rupiah(booking.early_checkin.fee_idr)})
                </span>
              </CheckboxRow>
            ) : null}
            <TotalRow>
              <span>{c.total}</span>
              <span>{rupiah(totalToPay)}</span>
            </TotalRow>
            <Note>{c.feeNote}</Note>
            <Note>{c.refundNote}</Note>

            {depositPayment.active ? (
              <>
                <ValidUntil>
                  {c.validUntil}{" "}
                  {formatDateTime(depositPayment.expires_at, language)}
                </ValidUntil>
                <Primary as="button" disabled={busy} onClick={openExisting}>
                  {c.openPayment}
                </Primary>
              </>
            ) : depositPayment.expired ? (
              <>
                <Note>{c.expiredNotice}</Note>
                <Primary disabled={busy} onClick={startCheckout}>
                  {busy ? <Loader2 size={16} className="spin" /> : c.regenerate}
                </Primary>
              </>
            ) : (
              <Primary disabled={busy} onClick={startCheckout}>
                {busy ? <Loader2 size={16} className="spin" /> : c.payNow}
              </Primary>
            )}
          </Card>
        ) : null}

        {step === "waiting" && booking ? (
          <Card>
            <Brand>
              <Loader2 size={20} className="spin" /> {c.waitingTitle}
            </Brand>
            <Note>{c.waitingBody}</Note>
            {depositPayment.payment_link_url ? (
              <Secondary
                as="a"
                href={depositPayment.payment_link_url}
                target="_blank"
                rel="noopener"
              >
                {c.openPayment}
              </Secondary>
            ) : null}
            <Primary
              disabled={refreshBusy || cooldown > 0}
              onClick={checkStatus}
            >
              {refreshBusy ? (
                <Loader2 size={16} className="spin" />
              ) : cooldown > 0 ? (
                `${c.checkStatus} (${cooldown})`
              ) : (
                c.checkStatus
              )}
            </Primary>
          </Card>
        ) : null}

        {step === "done" && booking ? (
          <SuccessView
            c={c}
            booking={booking}
            buildingCode={buildingCode}
            language={language}
            earlyOpened={earlyOpened}
            earlyPaid={earlyPaid}
            busy={busy}
            onBuyEarly={buyEarlyCheckin}
            onCheckEarly={checkEarly}
          />
        ) : null}
      </Inner>

      {showConfirm ? (
        <ModalOverlay onClick={() => setShowConfirm(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{c.confirmTitle}</ModalTitle>
            <ModalText>{c.confirmIntro}</ModalText>
            <ReviewRow>
              <ReviewLabel>{c.confirmEmailLabel}</ReviewLabel>
              <ReviewValue>{email}</ReviewValue>
            </ReviewRow>
            <ReviewRow>
              <ReviewLabel>{c.confirmWhatsappLabel}</ReviewLabel>
              <ReviewValue>{whatsapp}</ReviewValue>
            </ReviewRow>
            <WarnText>{c.confirmWarning}</WarnText>
            <ModalActions>
              <Ghost onClick={() => setShowConfirm(false)}>{c.edit}</Ghost>
              <Primary disabled={busy} onClick={saveContacts}>
                {busy ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  c.confirmContinue
                )}
              </Primary>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

const SuccessView = (props) => {
  const c = props.c;
  const booking = props.booking;
  const buildingCode = props.buildingCode;
  const early = booking.early_checkin || {};
  const [qrOpen, setQrOpen] = React.useState(false);

  return (
    <>
      <Card>
        <SuccessIcon>
          <CheckCircle2 size={26} />
        </SuccessIcon>
        <Brand>{c.success.title}</Brand>
        <Note>{c.success.body}</Note>

        <TimesBox>
          <TimesTitle>{c.success.timesTitle}</TimesTitle>
          <TimeRow>
            <span>{c.success.checkInLabel}</span>
            <strong>
              {formatDate(booking.check_in, props.language)} ·{" "}
              {c.success.checkInValue}
            </strong>
          </TimeRow>
          <TimeRow>
            <span>{c.success.checkOutLabel}</span>
            <strong>{formatDate(booking.check_out, props.language)}</strong>
          </TimeRow>
        </TimesBox>
      </Card>

      {booking.voucher_qr ? (
        <Card>
          <SectionTitle>
            <QrCode size={18} /> {c.success.qrTitle}
          </SectionTitle>
          <TutorialNote>{c.success.qrInstruction}</TutorialNote>
          <QrFigure
            type="button"
            onClick={() => setQrOpen(true)}
            aria-label={c.success.qrZoomHint}
          >
            <QrImage src={booking.voucher_qr} alt={c.success.qrTitle} />
            <QrZoomHint>{c.success.qrZoomHint}</QrZoomHint>
          </QrFigure>
          {booking.voucher_code ? (
            <QrCodeText>
              <span>{c.success.qrCodeLabel}</span>
              <strong>{booking.voucher_code}</strong>
            </QrCodeText>
          ) : null}
        </Card>
      ) : null}

      {booking.voucher_qr ? (
        <Card>
          <SectionTitle>
            <QrCode size={18} /> {c.success.scanTitle}
          </SectionTitle>
          <TutorialNote>{c.success.scanNote}</TutorialNote>
          <ScanImage src={checkinScanImg} alt={c.success.scanTitle} />
        </Card>
      ) : null}

      {qrOpen && booking.voucher_qr ? (
        <QrOverlay onClick={() => setQrOpen(false)}>
          <QrClose
            type="button"
            onClick={() => setQrOpen(false)}
            aria-label={c.success.qrCloseLabel}
          >
            <X size={24} />
          </QrClose>
          <QrOverlayInner onClick={(e) => e.stopPropagation()}>
            <QrImageLarge src={booking.voucher_qr} alt={c.success.qrTitle} />
            {booking.voucher_code ? (
              <QrOverlayCode>{booking.voucher_code}</QrOverlayCode>
            ) : null}
            <QrOverlayHint>{c.success.qrInstruction}</QrOverlayHint>
          </QrOverlayInner>
        </QrOverlay>
      ) : null}

      <Card>
        <SectionTitle>
          <Clock size={18} /> {c.success.tutorialTitle}
        </SectionTitle>
        <TutorialNote>{c.success.tutorialNote}</TutorialNote>
        <TutorialList>
          {c.success.steps.map((s, i) => (
            <TutorialItem key={s.title}>
              <TutorialNum>{i + 1}</TutorialNum>
              <div>
                <TutorialStepTitle>{s.title}</TutorialStepTitle>
                <TutorialStepDesc>{s.desc}</TutorialStepDesc>
              </div>
            </TutorialItem>
          ))}
        </TutorialList>
      </Card>

      {props.earlyPaid ? (
        <Card>
          <Brand>
            <CheckCircle2 size={20} /> {c.early.paid}
          </Brand>
        </Card>
      ) : early.available ? (
        <EarlyCard>
          <SectionTitle>{c.early.title}</SectionTitle>
          <EarlyNote>{c.early.availability}</EarlyNote>
          <EarlyNote>{c.early.significance}</EarlyNote>
          {props.earlyOpened ? (
            <>
              <Note>{c.early.opened}</Note>
              <Primary as="button" onClick={props.onCheckEarly}>
                {c.early.check}
              </Primary>
            </>
          ) : (
            <Primary disabled={props.busy} onClick={props.onBuyEarly}>
              {props.busy ? (
                <Loader2 size={16} className="spin" />
              ) : (
                `${c.early.button} (+${rupiah(early.fee_idr)})`
              )}
            </Primary>
          )}
        </EarlyCard>
      ) : null}

      <Card>
        <SectionTitle>{c.success.nextTitle}</SectionTitle>
        <NextLinks>
          <NextLink href={`/hebe/${buildingCode}/hgs/orders`}>
            {c.success.orderLink}
          </NextLink>
          <NextLink href={`/hebe/${buildingCode}/hgs/add-on-facility`}>
            {c.success.addOnLink}
          </NextLink>
          <NextLink href={`/hebe/${buildingCode}/hgs/fnb`}>
            {c.success.fnbLink}
          </NextLink>
        </NextLinks>
      </Card>
    </>
  );
};

const Summary = (props) => (
  <SummaryBox>
    <strong>{props.booking.guest_name}</strong>
    <span>
      {props.booking.nights} {props.c.nights} · {props.booking.rooms_count}{" "}
      {props.c.rooms} · {(props.booking.room_types || []).join(", ")}
    </span>
  </SummaryBox>
);

const STEP_ORDER = ["lookup", "otp", "contacts", "pay"];

const StepIndicator = (props) => {
  const index = STEP_ORDER.indexOf(props.step);
  if (index < 0) return null;
  return (
    <StepWrap>
      <StepBars>
        {STEP_ORDER.map((name, i) => (
          <StepBar key={name} $done={i <= index} />
        ))}
      </StepBars>
      <StepCaption>
        {props.c.step} {index + 1} {props.c.of} {STEP_ORDER.length} ·{" "}
        {props.c.stepNames[props.step]}
      </StepCaption>
    </StepWrap>
  );
};

const HowItWorks = (props) => (
  <HowBox>
    <HowTitle>{props.c.how.title}</HowTitle>
    {[props.c.how.s1, props.c.how.s2, props.c.how.s3, props.c.how.s4].map(
      (text, i) => (
        <HowItem key={text}>
          <HowNum>{i + 1}</HowNum>
          <span>{text}</span>
        </HowItem>
      ),
    )}
  </HowBox>
);

const Page = styled.div`
  background: #faf8f5;
  min-height: 100vh;
`;

const Inner = styled.div`
  margin: 0 auto;
  max-width: 480px;
  padding: 20px 16px 40px;
`;

const Brand = styled.div`
  align-items: center;
  color: #1a1a1a;
  display: flex;
  font-size: 20px;
  font-weight: 700;
  gap: 8px;
`;

const Lead = styled.p`
  color: #4b5563;
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 18px;
`;

const StepWrap = styled.div`
  margin-bottom: 16px;
`;

const StepBars = styled.div`
  display: flex;
  gap: 6px;
`;

const StepBar = styled.div`
  background: ${(p) => (p.$done ? "#c9a961" : "#e7e3db")};
  border-radius: 999px;
  flex: 1;
  height: 6px;
  transition: background 0.3s;
`;

const StepCaption = styled.div`
  color: #1a1a1a;
  font-size: 13px;
  font-weight: 600;
  margin-top: 8px;
`;

const HowBox = styled.div`
  background: #fff;
  border: 1px solid #efe9dd;
  border-radius: 14px;
  margin-bottom: 16px;
  padding: 16px 18px;
`;

const HowTitle = styled.div`
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const HowItem = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  span {
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const HowNum = styled.div`
  align-items: center;
  background: rgba(201, 169, 97, 0.15);
  border-radius: 50%;
  color: #9a7b2e;
  display: flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  height: 22px;
  justify-content: center;
  width: 22px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  margin-bottom: 14px;
  padding: 20px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  background: ${(p) => (p.$active ? "#1a1a1a" : "#f4f2ef")};
  border: none;
  border-radius: 8px;
  color: ${(p) => (p.$active ? "#fff" : "#555")};
  cursor: pointer;
  flex: 1;
  font-size: 13px;
  padding: 10px;
`;

const Field = styled.div`
  margin-bottom: 14px;

  label {
    color: #555;
    display: block;
    font-size: 13px;
    margin-bottom: 6px;
  }

  input {
    border: 1px solid #ddd;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 15px;
    padding: 11px 12px;
    width: 100%;
  }
`;

const Primary = styled.button`
  align-items: center;
  background: #c9a86c;
  border: none;
  border-radius: 9px;
  color: #1a1a1a;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  justify-content: center;
  margin-top: 6px;
  padding: 13px;
  text-decoration: none;
  width: 100%;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Secondary = styled.button`
  background: transparent;
  border: none;
  color: #c9a86c;
  cursor: pointer;
  display: block;
  font-size: 13px;
  margin: 8px 0;
  text-align: center;
  text-decoration: underline;
  width: 100%;
`;

const FindHeading = styled.div`
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;

  > button {
    flex: 1;
    margin-top: 0;
    min-width: 0;
  }
`;

const OutlineButton = styled.button`
  align-items: center;
  background: #fff;
  border: 1px solid #c9a86c;
  border-radius: 9px;
  color: #9a7b2e;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  justify-content: center;
  margin-top: 6px;
  padding: 13px;
  width: 100%;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ConsentRow = styled.label`
  align-items: flex-start;
  color: #374151;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  gap: 10px;
  line-height: 1.5;
  margin: 6px 0 14px;

  input {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const PolicyLink = styled.a`
  color: #9a7b2e;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
`;

const ValidUntil = styled.div`
  color: #8a6d1a;
  font-size: 13px;
  font-weight: 600;
  margin: 6px 0 2px;
  text-align: center;
`;

const ModalOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 16px;
  position: fixed;
  z-index: 50;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  border-top: 3px solid #c9a86c;
  max-width: 420px;
  padding: 22px;
  width: 100%;
`;

const ModalTitle = styled.h2`
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px;
`;

const ModalText = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 16px;
`;

const ReviewRow = styled.div`
  border-top: 1px solid #eee;
  padding: 12px 0;

  &:first-of-type {
    border-top: none;
  }
`;

const ReviewLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
  text-transform: uppercase;
`;

const ReviewValue = styled.div`
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 600;
  word-break: break-word;
`;

const WarnText = styled.p`
  background: #fdf6e7;
  border-radius: 8px;
  color: #8a6d1a;
  font-size: 13px;
  line-height: 1.5;
  margin: 14px 0 18px;
  padding: 10px 12px;
`;

const ModalActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;

  > button {
    margin-top: 0;
  }
`;

const Ghost = styled.button`
  background: #f4f2ef;
  border: none;
  border-radius: 9px;
  color: #1a1a1a;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 18px;
`;

const Note = styled.p`
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
  margin: 10px 0;
`;

const ErrorBox = styled.div`
  background: #fdecec;
  border-radius: 8px;
  color: #c0392b;
  font-size: 13px;
  margin-bottom: 14px;
  padding: 10px 12px;
`;

const SummaryBox = styled.div`
  background: #faf8f5;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  padding: 12px 14px;

  span {
    color: #4b5563;
    font-size: 13px;
  }
`;

const Row = styled.div`
  color: #333;
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  padding: 7px 0;
`;

const TotalRow = styled(Row)`
  border-top: 1px solid #eee;
  font-weight: 700;
  margin-top: 6px;
  padding-top: 12px;
`;

const CheckboxRow = styled.label`
  align-items: center;
  color: #333;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: 8px;
  padding: 8px 0;
`;

const SuccessIcon = styled.div`
  align-items: center;
  background: #e7f6ec;
  border-radius: 50%;
  color: #1f7a44;
  display: flex;
  height: 48px;
  justify-content: center;
  margin-bottom: 10px;
  width: 48px;
`;

const SectionTitle = styled.div`
  align-items: center;
  color: #1a1a1a;
  display: flex;
  font-size: 16px;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 6px;
`;

const TimesBox = styled.div`
  background: #faf8f5;
  border-radius: 10px;
  margin-top: 16px;
  padding: 12px 14px;
`;

const TimesTitle = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const TimeRow = styled.div`
  color: #333;
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  padding: 5px 0;
`;

const TutorialNote = styled.p`
  color: #8a6d1a;
  font-size: 13px;
  margin: 0 0 14px;
`;

const LoadingBox = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 48px 0;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const QrFigure = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #ececec;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 auto;
  padding: 16px;
  width: fit-content;
`;

const QrImage = styled.img`
  height: 200px;
  width: 200px;
`;

const ScanImage = styled.img`
  border: 1px solid #ececec;
  border-radius: 12px;
  display: block;
  margin-top: 10px;
  width: 100%;
`;

const QrZoomHint = styled.span`
  align-items: center;
  color: #c9a961;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 4px;
`;

const QrCodeText = styled.div`
  align-items: center;
  color: #1a1a1a;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 14px;
  text-align: center;

  span {
    color: #6b7280;
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  strong {
    font-size: 20px;
    letter-spacing: 0.08em;
  }
`;

const QrOverlay = styled.div`
  align-items: center;
  background: rgba(17, 17, 17, 0.92);
  display: flex;
  flex-direction: column;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1000;
`;

const QrClose = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  position: absolute;
  right: 16px;
  top: 16px;
`;

const QrOverlayInner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 420px;
`;

const QrImageLarge = styled.img`
  background: #ffffff;
  border-radius: 16px;
  height: min(78vw, 360px);
  padding: 16px;
  width: min(78vw, 360px);
`;

const QrOverlayCode = styled.div`
  color: #ffffff;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.1em;
`;

const QrOverlayHint = styled.p`
  color: #d9d9d9;
  font-size: 14px;
  line-height: 1.55;
  margin: 0;
  text-align: center;
`;

const TutorialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TutorialItem = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
`;

const TutorialNum = styled.div`
  align-items: center;
  background: #1a1a1a;
  border-radius: 50%;
  color: #fff;
  display: flex;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  width: 28px;
`;

const TutorialStepTitle = styled.div`
  color: #1a1a1a;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 3px;
`;

const TutorialStepDesc = styled.div`
  color: #4b5563;
  font-size: 13px;
  line-height: 1.55;
`;

const EarlyCard = styled(Card)`
  border: 1px solid #efe2c4;
  background: #fffdf7;
`;

const EarlyNote = styled.p`
  color: #6b5a2e;
  font-size: 13px;
  line-height: 1.55;
  margin: 4px 0;
`;

const NextLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const NextLink = styled(Link)`
  align-items: center;
  background: #f4f2ef;
  border-radius: 9px;
  color: #1a1a1a;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  justify-content: space-between;
  padding: 13px 16px;
  text-decoration: none;

  &:hover {
    background: #ece8e2;
  }
`;

export default SelfDeposit;
